import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FlagIcon from "@material-ui/icons/Flag";
import FlagOutlinedIcon from "@material-ui/icons/FlagOutlined";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe } from "@saleor/misc";
import { MotherOrderList_orders_edges_node } from "@saleor/motherOrders/types/MotherOrderList";
import { useRemittanceRunnerFlagOrder } from "@saleor/remittanceRunner/mutations";
import { useDialogStyles } from "@saleor/remittanceRunner/styles/dialogStyles";
import { RemittanceRunnerOrders_remittanceOrders_edges_node } from "@saleor/remittanceRunner/types/RemittanceRunnerOrders";
import { RemittanceManualAdjustmentStatusEnum } from "@saleor/types/globalTypes";
import * as React from "react";

const useStyles = makeStyles(
  _ => ({
    cursorHand: {
      cursor: "pointer"
    },
    flag: {
      margin: "3px 5px 0 0px"
    },
    flagDisabled: {
      color: "#CED3DD"
    },
    flagGreen: {
      color: "#00C6A2"
    },
    flagManualAdjust: {
      color: "#EC5D77"
    },
    flagUntag: {
      color: "#EC5D77"
    }
  }),
  { name: "RemittanceManualAdjustmentFlag" }
);

interface RemittanceManualAdjustmentFlagProps {
  isRemittanceOrder: boolean;
  disableAction?: boolean;
  order?: MotherOrderList_orders_edges_node;
  remittanceOrder?: RemittanceRunnerOrders_remittanceOrders_edges_node;
  onRefetchOrders: () => void;
}

const RemittanceManualAdjustmentFlag: React.FC<RemittanceManualAdjustmentFlagProps> = props => {
  const {
    isRemittanceOrder,
    disableAction,
    order,
    remittanceOrder,
    onRefetchOrders
  } = props;
  const classes = useStyles(props);
  const dialogClasses = useDialogStyles(props);
  const notify = useNotifier();

  const flag = !isRemittanceOrder
    ? order?.remittanceManualAdjustmentStatus
    : remittanceOrder?.remittanceManualAdjustmentStatus;
  const orderId = !isRemittanceOrder ? order?.id : remittanceOrder?.id;
  const orderRemittance = !isRemittanceOrder
    ? !!order?.remittanceLine
    : !!remittanceOrder?.remittanceLine;

  const [showConfirm, setShowConfirm] = React.useState(false);

  const flagComponent = () => {
    let flagComponent;
    if (
      orderRemittance &&
      flag !== RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT
    ) {
      flagComponent = (
        <FlagIcon className={`${classes.flag} ${classes.flagDisabled}`} />
      );
    } else if (flag === RemittanceManualAdjustmentStatusEnum.UNASSIGNED) {
      flagComponent = (
        <FlagOutlinedIcon className={`${classes.flag} ${classes.flagUntag}`} />
      );
    } else if (flag === RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT) {
      flagComponent = (
        <FlagIcon className={`${classes.flag} ${classes.flagManualAdjust}`} />
      );
    } else if (flag === RemittanceManualAdjustmentStatusEnum.COMPLETED) {
      flagComponent = (
        <FlagIcon className={`${classes.flag} ${classes.flagGreen}`} />
      );
    } else {
      flagComponent = null;
    }

    return flagComponent;
  };

  const [flagOrder] = useRemittanceRunnerFlagOrder({
    onCompleted: data => {
      if (data?.updateRemittanceManualAdjustmentStatus?.errors?.length === 0) {
        if (showConfirm) {
          setShowConfirm(false);
        }
        onRefetchOrders();
      } else if (
        data?.updateRemittanceManualAdjustmentStatus?.errors?.length > 0
      ) {
        data.updateRemittanceManualAdjustmentStatus.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    }
  });

  const flagClicked = () => {
    if (orderRemittance) {
      return;
    }

    if (flag === RemittanceManualAdjustmentStatusEnum.UNASSIGNED) {
      flagOrder({
        variables: {
          id: orderId,
          status: RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT
        }
      });
    } else if (flag === RemittanceManualAdjustmentStatusEnum.COMPLETED) {
      setShowConfirm(true);
    }
  };

  const manualAdjustAgain = () => {
    flagOrder({
      variables: {
        id: orderId,
        status: RemittanceManualAdjustmentStatusEnum.UNASSIGNED
      }
    });
  };

  return (
    <>
      {maybe(() => !disableAction) ? (
        <div className={classes.cursorHand} onClick={flagClicked}>
          {flagComponent()}
        </div>
      ) : null}

      <Dialog
        onClose={() => setShowConfirm(false)}
        open={showConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent className={dialogClasses.modal}>
          <IconButton
            aria-label="close"
            onClick={() => setShowConfirm(false)}
            className={dialogClasses.modalCloseBtn}
          >
            <CloseIcon />
          </IconButton>

          <div className={dialogClasses.marginTop50}>
            <h3 className={dialogClasses.modalTitleText}>Flag Order</h3>
            <h4
              className={dialogClasses.modalSubtitle}
              style={{ marginBottom: "15px" }}
            >
              Order has already been adjusted, are you sure you want to flag
              this order again?
            </h4>
          </div>

          <div className={dialogClasses.modalBottom}>
            <Button
              id="cancelDialogActionBtn"
              autoFocus
              onClick={() => setShowConfirm(false)}
              color="secondary"
              style={{ marginRight: 10 }}
              className={dialogClasses.btnCancel}
            >
              <span>Cancel</span>
            </Button>
            <Button
              id="confirmDialogActionBtn"
              autoFocus
              onClick={() => manualAdjustAgain()}
              color="primary"
              variant="contained"
              className={dialogClasses.btnYes}
            >
              <span className={dialogClasses.btnLblWhite}>Yes</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RemittanceManualAdjustmentFlag;
